export type CanvasEdgeRoutingMode = 'auto' | 'curve' | 'step' | 'straight';

export interface CanvasPoint {
  x: number;
  y: number;
}

export interface CanvasEdgeObstacle {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Rectangle {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

interface QueueItem {
  key: string;
  pointIndex: number;
  direction: 'horizontal' | 'vertical' | 'start';
  cost: number;
}

const COORDINATE_PRECISION = 3;
const EPSILON = 0.01;
const BEND_PENALTY = 18;

const roundCoordinate = (value: number) => Number(value.toFixed(COORDINATE_PRECISION));
const coordinateKey = (value: number) => roundCoordinate(value).toFixed(COORDINATE_PRECISION);
const pointKey = (point: CanvasPoint) => `${coordinateKey(point.x)}:${coordinateKey(point.y)}`;

const uniqueCoordinates = (values: number[]) =>
  Array.from(new Set(values.filter(Number.isFinite).map(roundCoordinate))).sort((a, b) => a - b);

const expandObstacle = (obstacle: CanvasEdgeObstacle, clearance: number): Rectangle => ({
  left: obstacle.x - clearance,
  right: obstacle.x + obstacle.width + clearance,
  top: obstacle.y - clearance,
  bottom: obstacle.y + obstacle.height + clearance,
});

const isPointInsideRectangle = (point: CanvasPoint, rectangle: Rectangle) =>
  point.x > rectangle.left + EPSILON &&
  point.x < rectangle.right - EPSILON &&
  point.y > rectangle.top + EPSILON &&
  point.y < rectangle.bottom - EPSILON;

export const segmentIntersectsRectangle = (
  start: CanvasPoint,
  end: CanvasPoint,
  rectangle: Rectangle,
) => {
  if (Math.abs(start.y - end.y) <= EPSILON) {
    const y = start.y;
    if (y <= rectangle.top + EPSILON || y >= rectangle.bottom - EPSILON) return false;

    const segmentLeft = Math.min(start.x, end.x);
    const segmentRight = Math.max(start.x, end.x);
    return segmentRight > rectangle.left + EPSILON && segmentLeft < rectangle.right - EPSILON;
  }

  if (Math.abs(start.x - end.x) <= EPSILON) {
    const x = start.x;
    if (x <= rectangle.left + EPSILON || x >= rectangle.right - EPSILON) return false;

    const segmentTop = Math.min(start.y, end.y);
    const segmentBottom = Math.max(start.y, end.y);
    return segmentBottom > rectangle.top + EPSILON && segmentTop < rectangle.bottom - EPSILON;
  }

  return true;
};

const isSegmentClear = (start: CanvasPoint, end: CanvasPoint, rectangles: Rectangle[]) =>
  rectangles.every((rectangle) => !segmentIntersectsRectangle(start, end, rectangle));

const simplifyOrthogonalPoints = (points: CanvasPoint[]) => {
  if (points.length <= 2) return points;

  const simplified: CanvasPoint[] = [points[0]];

  for (let index = 1; index < points.length - 1; index += 1) {
    const previous = simplified[simplified.length - 1];
    const current = points[index];
    const next = points[index + 1];
    const isVertical = Math.abs(previous.x - current.x) <= EPSILON && Math.abs(current.x - next.x) <= EPSILON;
    const isHorizontal = Math.abs(previous.y - current.y) <= EPSILON && Math.abs(current.y - next.y) <= EPSILON;

    if (!isVertical && !isHorizontal) simplified.push(current);
  }

  simplified.push(points[points.length - 1]);
  return simplified;
};

class MinQueue {
  private items: QueueItem[] = [];

  get size() {
    return this.items.length;
  }

  push(item: QueueItem) {
    this.items.push(item);
    let index = this.items.length - 1;

    while (index > 0) {
      const parentIndex = Math.floor((index - 1) / 2);
      if (this.items[parentIndex].cost <= item.cost) break;
      this.items[index] = this.items[parentIndex];
      index = parentIndex;
    }

    this.items[index] = item;
  }

  pop(): QueueItem | undefined {
    if (this.items.length === 0) return undefined;

    const first = this.items[0];
    const last = this.items.pop();
    if (!last || this.items.length === 0) return first;

    let index = 0;
    while (true) {
      const left = index * 2 + 1;
      const right = left + 1;
      if (left >= this.items.length) break;

      const smallerChild = right < this.items.length && this.items[right].cost < this.items[left].cost
        ? right
        : left;
      if (this.items[smallerChild].cost >= last.cost) break;

      this.items[index] = this.items[smallerChild];
      index = smallerChild;
    }

    this.items[index] = last;
    return first;
  }
}

const connectVisibleNeighbors = (
  groupedIndices: Map<string, number[]>,
  points: CanvasPoint[],
  neighbors: Map<number, number[]>,
  rectangles: Rectangle[],
  axis: 'x' | 'y',
) => {
  groupedIndices.forEach((indices) => {
    indices.sort((leftIndex, rightIndex) => (
      axis === 'x'
        ? points[leftIndex].y - points[rightIndex].y
        : points[leftIndex].x - points[rightIndex].x
    ));

    for (let index = 0; index < indices.length - 1; index += 1) {
      const firstIndex = indices[index];
      const secondIndex = indices[index + 1];
      if (!isSegmentClear(points[firstIndex], points[secondIndex], rectangles)) continue;

      neighbors.get(firstIndex)?.push(secondIndex);
      neighbors.get(secondIndex)?.push(firstIndex);
    }
  });
};

/**
 * Builds a small orthogonal visibility graph around the canvas nodes and finds
 * a shortest route with a bend penalty. Callers can exclude endpoint nodes, or
 * keep them when the source and target port stubs sit outside expanded bounds.
 */
export const getObstacleAvoidingPoints = (
  source: CanvasPoint,
  target: CanvasPoint,
  obstacles: CanvasEdgeObstacle[],
  clearance = 22,
): CanvasPoint[] => {
  const rectangles = obstacles
    .filter((obstacle) => (
      Number.isFinite(obstacle.x) &&
      Number.isFinite(obstacle.y) &&
      Number.isFinite(obstacle.width) &&
      Number.isFinite(obstacle.height) &&
      obstacle.width > 0 &&
      obstacle.height > 0
    ))
    .map((obstacle) => expandObstacle(obstacle, clearance));

  if (isSegmentClear(source, target, rectangles) && (
    Math.abs(source.x - target.x) <= EPSILON || Math.abs(source.y - target.y) <= EPSILON
  )) {
    return [source, target];
  }

  const xCoordinates = uniqueCoordinates([
    source.x,
    target.x,
    ...rectangles.flatMap((rectangle) => [rectangle.left, rectangle.right]),
  ]);
  const yCoordinates = uniqueCoordinates([
    source.y,
    target.y,
    ...rectangles.flatMap((rectangle) => [rectangle.top, rectangle.bottom]),
  ]);

  const points: CanvasPoint[] = [];
  const indexByPoint = new Map<string, number>();

  const addPoint = (point: CanvasPoint, allowInside = false) => {
    const normalized = { x: roundCoordinate(point.x), y: roundCoordinate(point.y) };
    if (!allowInside && rectangles.some((rectangle) => isPointInsideRectangle(normalized, rectangle))) return;

    const key = pointKey(normalized);
    if (indexByPoint.has(key)) return;
    indexByPoint.set(key, points.length);
    points.push(normalized);
  };

  xCoordinates.forEach((x) => {
    yCoordinates.forEach((y) => addPoint({ x, y }));
  });
  addPoint(source, true);
  addPoint(target, true);

  const sourceIndex = indexByPoint.get(pointKey(source));
  const targetIndex = indexByPoint.get(pointKey(target));
  if (sourceIndex === undefined || targetIndex === undefined) return [source, target];

  const neighbors = new Map<number, number[]>();
  const pointsByX = new Map<string, number[]>();
  const pointsByY = new Map<string, number[]>();

  points.forEach((point, index) => {
    neighbors.set(index, []);
    const xKey = coordinateKey(point.x);
    const yKey = coordinateKey(point.y);
    pointsByX.set(xKey, [...(pointsByX.get(xKey) || []), index]);
    pointsByY.set(yKey, [...(pointsByY.get(yKey) || []), index]);
  });

  connectVisibleNeighbors(pointsByX, points, neighbors, rectangles, 'x');
  connectVisibleNeighbors(pointsByY, points, neighbors, rectangles, 'y');

  const queue = new MinQueue();
  const bestCost = new Map<string, number>();
  const previous = new Map<string, string>();
  const startKey = `${sourceIndex}:start`;

  bestCost.set(startKey, 0);
  queue.push({ key: startKey, pointIndex: sourceIndex, direction: 'start', cost: 0 });

  let finalKey: string | null = null;

  while (queue.size > 0) {
    const current = queue.pop();
    if (!current || current.cost !== bestCost.get(current.key)) continue;

    if (current.pointIndex === targetIndex) {
      finalKey = current.key;
      break;
    }

    (neighbors.get(current.pointIndex) || []).forEach((neighborIndex) => {
      const currentPoint = points[current.pointIndex];
      const neighborPoint = points[neighborIndex];
      const nextDirection = Math.abs(currentPoint.x - neighborPoint.x) <= EPSILON
        ? 'vertical'
        : 'horizontal';
      const distance = Math.abs(currentPoint.x - neighborPoint.x) + Math.abs(currentPoint.y - neighborPoint.y);
      const bendCost = current.direction !== 'start' && current.direction !== nextDirection ? BEND_PENALTY : 0;
      const nextCost = current.cost + distance + bendCost;
      const nextKey = `${neighborIndex}:${nextDirection}`;

      if (nextCost >= (bestCost.get(nextKey) ?? Number.POSITIVE_INFINITY)) return;

      bestCost.set(nextKey, nextCost);
      previous.set(nextKey, current.key);
      queue.push({
        key: nextKey,
        pointIndex: neighborIndex,
        direction: nextDirection,
        cost: nextCost,
      });
    });
  }

  if (!finalKey) return [source, target];

  const path: CanvasPoint[] = [];
  let cursor: string | undefined = finalKey;

  while (cursor) {
    const pointIndex = Number(cursor.split(':')[0]);
    path.push(points[pointIndex]);
    cursor = previous.get(cursor);
  }

  return simplifyOrthogonalPoints(path.reverse());
};

export const getPolylineMidpoint = (points: CanvasPoint[]): CanvasPoint => {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return points[0];

  const segmentLengths = points.slice(1).map((point, index) => (
    Math.hypot(point.x - points[index].x, point.y - points[index].y)
  ));
  const totalLength = segmentLengths.reduce((total, length) => total + length, 0);
  let remaining = totalLength / 2;

  for (let index = 0; index < segmentLengths.length; index += 1) {
    const segmentLength = segmentLengths[index];
    if (remaining > segmentLength) {
      remaining -= segmentLength;
      continue;
    }

    const start = points[index];
    const end = points[index + 1];
    const ratio = segmentLength === 0 ? 0 : remaining / segmentLength;
    return {
      x: start.x + (end.x - start.x) * ratio,
      y: start.y + (end.y - start.y) * ratio,
    };
  }

  return points[points.length - 1];
};

export const getRoundedPolylinePath = (points: CanvasPoint[], radius = 10) => {
  if (points.length === 0) return { path: '', labelX: 0, labelY: 0 };
  if (points.length === 1) return { path: `M ${points[0].x} ${points[0].y}`, labelX: points[0].x, labelY: points[0].y };

  let path = `M ${points[0].x} ${points[0].y}`;

  for (let index = 1; index < points.length - 1; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const next = points[index + 1];
    const incomingLength = Math.hypot(current.x - previous.x, current.y - previous.y);
    const outgoingLength = Math.hypot(next.x - current.x, next.y - current.y);
    const cornerRadius = Math.min(radius, incomingLength / 2, outgoingLength / 2);

    if (cornerRadius <= EPSILON) {
      path += ` L ${current.x} ${current.y}`;
      continue;
    }

    const before = {
      x: current.x - ((current.x - previous.x) / incomingLength) * cornerRadius,
      y: current.y - ((current.y - previous.y) / incomingLength) * cornerRadius,
    };
    const after = {
      x: current.x + ((next.x - current.x) / outgoingLength) * cornerRadius,
      y: current.y + ((next.y - current.y) / outgoingLength) * cornerRadius,
    };

    path += ` L ${before.x} ${before.y} Q ${current.x} ${current.y} ${after.x} ${after.y}`;
  }

  const last = points[points.length - 1];
  path += ` L ${last.x} ${last.y}`;
  const midpoint = getPolylineMidpoint(points);

  return { path, labelX: midpoint.x, labelY: midpoint.y };
};
